// useBillingRevenue — the job's billable amount, for pages other than Billing.
//
// The Overview VoltaGrid tab shows revenue, and on a cost-plus job revenue is
// what the cost to date bills for: the Billing tab's billable amount. This
// hook assembles the same inputs the Billing tab prices with (its stored rows
// at BILLING_VERSION, the vendor-invoice match status, the field ticket
// workbook's class map, the job's cost history) and runs billingPricing's
// pricing pass over them — so the number here IS the Billing tab's, not a
// re-derivation of it.
//
// Off (`enabled: false`) it loads nothing and returns the empty shape, so a
// page can call it unconditionally and pay only on the jobs that need it.
// The billing rows land on the shared billingUserDataAtom in the same shape
// the Billing tab puts there, so whichever view loads first, the other reads
// it instead of fetching again.

import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { loadUserData, loadPOMatchStatus, loadFieldTicketMap, loadAttendanceRoster, loadRentalUsage } from '../db/company';
import { selectedJobAtom, jobCostDetailAtom, billingUserDataAtom, BILLING_VERSION } from '../store/company';
import { fieldSuggestions } from './billingLaborMap';
import { contractOn, equipRowAs } from './billingContract';
import { rentalUsageOf, rentalElsewhereOf, rentalPosOf, NO_RENT_ELSEWHERE } from './billingRentalUsage';
import { equipNamer, unitTiersOf, rentalItemsOf } from './billingEquipNames';
import { mergeRateSchedule } from './billingRates';
import {
    parseBillingRows, poStatusMap, jdeLaborClassesOf, jobEquipTiersOf, equipBillsOf, billsAsOf, sheetLaborOf, fuelCtxOf, rateCfgOf,
    priceCostRows, billableRevenue, personClsOf,
} from './billingPricing';

const EMPTY = { ready: false, loading: false, error: null, bill: 0, fuel: 0, billed: 0, unbilled: 0, cost: 0, nbCost: 0, lines: 0, noRate: { n: 0, cost: 0, hrs: 0 }, txns: [], fuelCtx: null, billsAs: {}, personCls: {}, sheetLabor: null, rateCfg: null, invoices: [] };

export default function useBillingRevenue({ enabled = true } = {}) {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [billingData, setBillingData] = useAtom(billingUserDataAtom);
    const billingLoaded = !!billingData && billingData.job === selectedJob && Array.isArray(billingData.rows);
    const billingError = !!billingData && billingData.job === selectedJob && billingData.error ? billingData.error : null;

    // The job's Billing rows — same load and same atom shape as the Billing
    // tab, keyed on the job only so a save's mirror can't refetch.
    useEffect(() => {
        if (!enabled || !selectedJob) return undefined;
        if (billingData && billingData.job === selectedJob && (Array.isArray(billingData.rows) || billingData.error)) return undefined;
        let stale = false;
        loadUserData({ id: selectedJob, forecastGuid: BILLING_VERSION, live: true })
            .then((res) => {
                if (stale) return;
                if (!res || res.error) throw new Error(res?.error || 'no response');
                const rows = (Array.isArray(res.data) ? res.data : []).filter((x) => x?.Forecast_Tab === 'Billing');
                setBillingData({ job: selectedJob, rows, error: null });
            })
            .catch((e) => {
                if (stale) return;
                setBillingData({ job: selectedJob, rows: null, error: String(e?.message || e || 'load failed') });
            });
        return () => { stale = true; };
    }, [enabled, selectedJob]); // only the job: billingData is read, not tracked

    // Vendor-invoice match status per PO line: a reversed voucher takes its
    // line off the invoice, so the billable amount needs it too.
    const [poStatus, setPoStatus] = useState({});
    useEffect(() => {
        setPoStatus({});
        if (!enabled || !selectedJob) return undefined;
        let stale = false;
        loadPOMatchStatus({ id: selectedJob })
            .then((res) => { if (!stale) setPoStatus(poStatusMap(res?.data)); })
            .catch(() => { /* lines just price without the match status */ });
        return () => { stale = true; };
    }, [enabled, selectedJob]);

    // The field ticket workbook's Staff List — the fallback class map for
    // labor the Rates & Rules map hasn't pinned.
    const [fieldMap, setFieldMap] = useState(null);
    useEffect(() => {
        setFieldMap(null);
        if (!enabled || !selectedJob) return undefined;
        let dead = false;
        loadFieldTicketMap({ job: String(selectedJob) })
            .then((res) => { if (!dead) setFieldMap(res && res.found ? res : null); })
            .catch(() => { if (!dead) setFieldMap(null); });
        return () => { dead = true; };
    }, [enabled, selectedJob]);

    // The Attendance & Force Report roster — each person's own billing
    // classification (personClsOf). Priced without it until it lands, then
    // re-priced; a job with no roster just prices off the class map.
    const [roster, setRoster] = useState(null);
    useEffect(() => {
        setRoster(null);
        if (!enabled || !selectedJob) return undefined;
        let dead = false;
        loadAttendanceRoster({ job: String(selectedJob) })
            .then((res) => { if (!dead) setRoster(res && res.found && Array.isArray(res.roster) ? res.roster : []); })
            .catch(() => { if (!dead) setRoster([]); });
        return () => { dead = true; };
    }, [enabled, selectedJob]);

    // HeavyJob usage of the job's rental units — rentals bill hours × the
    // rate sheet rate off it, same as on the Billing tab. null (not loaded,
    // or no function) prices the rental vouchers at cost + % instead.
    const rentPoKey = useMemo(() => rentalPosOf(jobCostDetail).join(','), [jobCostDetail]);
    const [rentUse, setRentUse] = useState({ key: '', data: null, elsewhere: [] });
    useEffect(() => {
        const key = `${selectedJob}|${rentPoKey}`;
        if (!enabled || !selectedJob) return undefined;
        let dead = false;
        Promise.resolve()
            .then(() => loadRentalUsage({ job: String(selectedJob), pos: rentPoKey ? rentPoKey.split(',') : [] }))
            .then((res) => { if (!dead) setRentUse({ key, data: rentalUsageOf(res), elsewhere: rentalElsewhereOf(res) }); })
            .catch(() => { if (!dead) setRentUse({ key, data: null, elsewhere: [] }); });
        return () => { dead = true; };
    }, [enabled, selectedJob, rentPoKey]);
    const rentLoaded = rentUse.key === `${selectedJob}|${rentPoKey}`;
    const rentalUsage = rentLoaded ? rentUse.data : null;
    const rentalElsewhere = rentLoaded ? rentUse.elsewhere : NO_RENT_ELSEWHERE;

    return useMemo(() => {
        if (!enabled) return EMPTY;
        if (billingError) return { ...EMPTY, error: billingError };
        if (!billingLoaded || !Array.isArray(jobCostDetail)) return { ...EMPTY, loading: true };
        const rows = billingData.rows.filter((x) => x?.Forecast_Tab === 'Billing');
        const { billed, nbLines, settings, metas, invoices } = parseBillingRows(rows, selectedJob);
        const jdeLaborClasses = jdeLaborClassesOf(jobCostDetail);
        const jobEquipTiers = jobEquipTiersOf(jobCostDetail, rentalUsage);
        const equipBills = equipBillsOf(rows, jobEquipTiers);
        const billsAs = billsAsOf(rows, jdeLaborClasses, fieldSuggestions(fieldMap?.rows));
        const sheetLabor = sheetLaborOf(rows);
        const personCls = personClsOf(roster, sheetLabor);
        const contractRules = contractOn(settings, selectedJob);
        const fuelCtx = fuelCtxOf(rows, settings);
        const txns = priceCostRows(jobCostDetail, { billed, nbLines, metas, settings, poStatus, billsAs, equipBills, sheetLabor, personCls, rateCfg: rateCfgOf(rows), contractRules, contractJob: selectedJob, rentalUsage, rentalElsewhere });
        // billsAs (JDE class → sheet class + target wage), the per-person
        // map, the sheet index and the rate config ride along for the
        // Overview's paid-rate-vs-target labor table. The summaries (with
        // their stage) tell the Cost Plus Forecast which lines went out.
        // `priceCtx` is the same pricing context without this job's billed
        // state, for the Cost Plus Forecast to price the cost to go with
        // (billingPricing's priceToGo).
        const priceCtx = { settings, billsAs, equipBills, sheetLabor, personCls, contractRules, contractJob: selectedJob };
        // (unit, description) => the rate sheet item a unit prints under, as on
        // the tickets (billing.js builds the same namer), for the Overview's
        // equipment table. A rental nothing else names falls back to its PO
        // line's item ("120-TON RT CRANE" for a line HeavyJob calls "Bare Rate").
        const equipRows = mergeRateSchedule(rows).equip;
        const equipName = equipNamer({ unitTiers: unitTiersOf(jobCostDetail, rentalUsage), equipBills, equipRows, rentalItems: rentalItemsOf(rentalUsage), equipAs: equipRowAs(selectedJob) });
        return { ...billableRevenue(txns, fuelCtx), ready: true, loading: false, error: null, txns, fuelCtx, billsAs, personCls, sheetLabor, rateCfg: rateCfgOf(rows), invoices, priceCtx, equipName, equipRows };
    }, [enabled, billingError, billingLoaded, billingData, jobCostDetail, fieldMap, roster, poStatus, selectedJob, rentalUsage, rentalElsewhere]);
}
